import React from "react";
import Marquee from "react-fast-marquee";
import { motion } from "framer-motion";
import { MapPin, Clock, Phone } from "lucide-react";
import { BRAND } from "../data/site";

const KL_IMG =
  "https://images.unsplash.com/photo-1661486668495-668e7f959316?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwxfHxrdWFsYSUyMGx1bXB1ciUyMG5pZ2h0JTIwc2t5bGluZXxlbnwwfHx8fDE3NzcyOTIxMDJ8MA&ixlib=rb-4.1.0&q=85";

const marqueeTokens = [
  "Penang",
  "Kuala Lumpur",
  "Johor",
  "George Town",
  "Bukit Bintang",
  "Johor Bahru",
  "Butterworth",
  "Cheras",
  "Iskandar Puteri",
];

export default function Locations() {
  return (
    <section
      id="locations"
      data-testid="locations-section"
      className="relative bg-black overflow-hidden"
    >
      {/* Marquee band */}
      <div className="py-10 border-y border-white/10 marquee-mask">
        <Marquee gradient={false} speed={40} pauseOnHover>
          {marqueeTokens.concat(marqueeTokens).map((t, i) => (
            <span
              key={i}
              className="font-display text-5xl md:text-7xl font-black uppercase tracking-tighter text-white/5 mx-8 whitespace-nowrap"
              style={
                i % 3 === 1
                  ? { WebkitTextStroke: "1px rgba(255,255,255,0.25)", color: "transparent" }
                  : i % 3 === 2
                  ? { color: "#FF3B30" }
                  : undefined
              }
            >
              {t} ·
            </span>
          ))}
        </Marquee>
      </div>

      {/* Skyline hero */}
      <div className="relative h-[60vh] md:h-[70vh] w-full overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${KL_IMG})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-black/20" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-transparent to-transparent" />

        <div className="relative z-10 h-full max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 flex flex-col justify-end pb-16">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="max-w-3xl"
          >
            <div className="text-[11px] uppercase tracking-[0.3em] text-[#FF3B30] mb-4">
              — Where we work
            </div>
            <h2 className="font-display text-4xl md:text-6xl lg:text-7xl font-black uppercase tracking-tighter leading-[0.9]">
              Three cities.
              <br />
              <span className="text-outline">Zero borders.</span>
            </h2>
            <p className="mt-6 text-white/70 text-base md:text-lg max-w-xl">
              We work fully remote — on‑location shoots, virtual war‑rooms and
              same‑day Zooms across Penang, KL and Johor.
            </p>
          </motion.div>
        </div>
      </div>

      {/* Location cards */}
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 py-20">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {BRAND.locations.map((loc, idx) => (
            <motion.div
              key={loc.city}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.55, delay: idx * 0.08 }}
              data-testid={`location-card-${loc.city.toLowerCase().replace(/\s+/g, "-")}`}
              className="bento-card p-8 md:p-10 min-h-[260px] flex flex-col justify-between"
            >
              <div>
                <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-white/40 mb-6">
                  <span className="inline-block w-1.5 h-1.5 bg-[#FF3B30]" />
                  Coverage · 0{idx + 1}
                </div>
                <h3 className="font-display text-4xl md:text-5xl font-black uppercase">
                  {loc.city}
                </h3>
              </div>

              <div className="space-y-4 text-sm text-white/70 mt-8">
                <div className="flex gap-3">
                  <MapPin size={16} className="text-[#FF3B30] shrink-0 mt-0.5" />
                  {loc.coverage}
                </div>
                <div className="flex gap-3">
                  <Clock size={16} className="text-[#FF3B30] shrink-0 mt-0.5" />
                  {loc.hours}
                </div>
                <div className="flex gap-3">
                  <Phone size={16} className="text-[#FF3B30] shrink-0 mt-0.5" />
                  {BRAND.phone}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
